import { useEffect, useState, useSyncExternalStore } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getMe } from '@/api/auth';
import AiHelpers from '@/components/dashboard/AiHelpers';
import MigrationCheckupBanner, { useCheckupHidden } from '@/components/dashboard/MigrationCheckupBanner';
import WidgetGrid from '@/components/dashboard/WidgetGrid';
import WidgetSettings from '@/components/dashboard/WidgetSettings';
import { usePermissions } from '@/auth/usePermissions';
import {
  WIDGETS, WIDGETS_PREF_KEY, WIDGETS_HIDDEN_KEY, effectiveWidgetIds,
} from '@/components/dashboard/widgetRegistry';
import uiPrefs from '@/store/uiPrefs';
import AddOrganizationCta from '@/components/AddOrganizationCta';
import { useOnboarding } from '@/hooks/useOnboarding';

/**
 * Auto-redirect для CASHIER_POS-only ролей (Порція Роздріб.6).
 *
 * Якщо у user серед access_role_codes є виключно касирські ролі
 * (CASHIER_POS / CASHIER_SENIOR) — авто-редіректимо на /pos.
 * Якщо є хоча б одна бух-роль — лишаємо у бух-UI (user сам обере).
 */
const CASHIER_ONLY_ROLES = new Set(['CASHIER_POS', 'CASHIER_SENIOR']);

function shouldAutoRedirectToPos(roles: string[] | undefined): boolean {
  if (!roles || roles.length === 0) return false;
  return roles.every((r) => CASHIER_ONLY_ROLES.has(r));
}

export default function HomePage() {
  const navigate = useNavigate();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: getMe });
  const { onboarding, edoOnly } = useOnboarding();
  // ч.843: набір віджетів — з профілю (uiPrefs), з дефолтом при першому вході.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const checkupHidden = useCheckupHidden();
  const storedIds = useSyncExternalStore(
    (cb) => uiPrefs.subscribe(WIDGETS_PREF_KEY, cb),
    () => uiPrefs.get<string[]>(WIDGETS_PREF_KEY),
    () => null,
  );
  // ч.1364: віджет, доданий після того, як користувач зберіг свій набір, показується сам,
  // доки його не приберуть у налаштуваннях (прибрані — окремий ключ).
  const hiddenIds = useSyncExternalStore(
    (cb) => uiPrefs.subscribe(WIDGETS_HIDDEN_KEY, cb),
    () => uiPrefs.get<string[]>(WIDGETS_HIDDEN_KEY),
    () => null,
  );
  // ч.1311 (власник: «під менеджером з продажу всюди червоний бейдж»): віджет, на дані якого
  // у ролі немає права, не показується і не запитує сервер (раніше кожен відповідав 403).
  const permissions = usePermissions();
  // Поки права не завантажились — віджетів не монтуємо: інакше вони встигають надіслати
  // запити й отримати 403 до того, як їх відфільтрують.
  const allowed = (w: { permission?: string } | undefined) =>
    !!w && !permissions.isLoading && (!w.permission || permissions.has(w.permission));
  const availableWidgets = WIDGETS.filter(allowed);
  const widgetIds = effectiveWidgetIds(storedIds, hiddenIds)
    .filter((id) => availableWidgets.some((w) => w.id === id));

  useEffect(() => {
    if (!meQuery.data) return;
    // OWNER/ADMIN — access_role_codes завжди порожній; залишаємо у бух-UI.
    if (meQuery.data.tenant_role !== 'MEMBER') return;
    if (shouldAutoRedirectToPos(meQuery.data.access_role_codes)) {
      navigate('/pos', { replace: true });
    }
  }, [meQuery.data, navigate]);

  // ч.1405: у режимі «ЕДО» віджети обліку недоречні — головна веде у вхідні документи.
  if (edoOnly) return <Navigate to="/edo/journal" replace />;
  // ч.1072: в акаунті ще немає організацій — віджетам нема чого показувати.
  if (onboarding) {
    return (
      <div className="card" style={{ maxWidth: 640 }}>
        <h2 style={{ marginTop: 0 }}>Поки немає доступних організацій</h2>
        <AddOrganizationCta variant="page" />
      </div>
    );
  }

  return (
    <>
      {/* ч.1099 (рішення власника): замість «Ласкаво просимо» — помічники Oblik+/AI. */}
      <AiHelpers />
      {/* ч.1411 (рішення власника): кнопка — над чек-апом, ним теж керує. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
        <button type="button" onClick={() => setSettingsOpen(true)}
          title="Додати або прибрати віджети і чек-ап на головній сторінці">
          налаштувати віджети
        </button>
      </div>
      {/* ч.1112 (рішення власника): нагадування з чек-апу після міграції — не віджет. */}
      {!checkupHidden && <MigrationCheckupBanner />}
      <WidgetGrid>
        {widgetIds.map((id) => {
          const w = WIDGETS.find((x) => x.id === id)!;
          return <div key={id}>{w.render()}</div>;
        })}
      </WidgetGrid>
      {!permissions.isLoading && widgetIds.length === 0 && (
        <div style={{ color: 'var(--muted)', marginTop: '0.75rem' }}>
          {availableWidgets.length === 0
            ? 'Для вашої ролі віджетів головної немає — користуйтесь меню зверху.'
            : 'Віджети не обрано — «налаштувати віджети».'}
        </div>
      )}
      {settingsOpen && (
        <WidgetSettings
          available={availableWidgets}
          selected={widgetIds}
          onChange={(ids) => {
            uiPrefs.set(WIDGETS_PREF_KEY, ids);
            uiPrefs.set(WIDGETS_HIDDEN_KEY, availableWidgets.map((w) => w.id).filter((id) => !ids.includes(id)));
          }}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </>
  );
}

export { shouldAutoRedirectToPos };

